import { useState, useCallback, useMemo } from 'react';
import { Button, TextField, Select, Tag } from '@cimpress/react-components';
import {
  IconPencilAlt,
  IconBin,
} from '@cimpress-technology/react-streamline-icons';
import {
  BaseEditableItem,
  EditableListConfig,
  SelectOption,
} from '../models/EditableList';
import {
  generateId,
  validateField,
  getStyleClass,
  toDisplayName,
} from '../utilities/common';
import { FIELD_STYLE } from '../constants';
import ActionButtons from './shared/ActionButtons';
import styles from '../css/pages.module.scss';

interface InputRowProps {
  config: EditableListConfig;
  onAdd: (primaryValue: string, secondaryValue: string) => void;
  onCancel: () => void;
  existingNames: string[];
}

interface ItemRowProps<T extends BaseEditableItem> {
  item: T;
  config: EditableListConfig;
  getPrimaryValue: (item: T) => string;
  getSecondaryValue: (item: T) => string;
  onDelete: (id: string) => void;
  onUpdate: (id: string, primaryValue: string, secondaryValue: string) => void;
  existingNames: string[];
  fullPrimaryOptions?: SelectOption[];
  isReadOnly?: boolean;
}

interface EditableListTabProps<T extends BaseEditableItem> {
  items: T[];
  config: EditableListConfig;
  getPrimaryValue: (item: T) => string;
  getSecondaryValue: (item: T) => string;
  onChange: (items: T[]) => void;
  createItem: (id: string, primaryValue: string, secondaryValue: string) => T;
  updateItem: (item: T, primaryValue: string, secondaryValue: string) => T;
  fullPrimaryOptions?: SelectOption[];
  readOnlyIds?: string[];
}

// Input Row Component for adding new items
const InputRow = ({
  config,
  onAdd,
  onCancel,
  existingNames,
}: InputRowProps) => {
  const [primaryValue, setPrimaryValue] = useState('');
  const [primarySelectValue, setPrimarySelectValue] =
    useState<SelectOption | null>(null);
  const [secondaryValue, setSecondaryValue] = useState<SelectOption | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const isPrimarySelect = config.primaryField.type === 'select';
  const primaryFieldLabel = config.primaryField.label;

  const getPrimaryValueString = useCallback(
    () => (isPrimarySelect ? primarySelectValue?.value || '' : primaryValue),
    [isPrimarySelect, primarySelectValue, primaryValue],
  );

  const containerClass = getStyleClass(
    styles,
    config.stylePrefix,
    'InputRow',
    'editableInputRow',
  );
  const fieldsClass = getStyleClass(
    styles,
    config.stylePrefix,
    'InputFields',
    'editableInputFields',
  );

  const handleAdd = () => {
    const primaryVal = getPrimaryValueString();
    const validationError = validateField(
      primaryVal,
      primaryFieldLabel,
      existingNames,
    );
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!secondaryValue) {
      setError(`${config.secondaryField.label} is required`);
      return;
    }
    onAdd(primaryVal.trim(), secondaryValue.value);
    setPrimaryValue('');
    setPrimarySelectValue(null);
    setSecondaryValue(null);
    setError(null);
  };

  const handlePrimaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPrimaryValue(newValue);
    const validationError = validateField(
      newValue,
      primaryFieldLabel,
      existingNames,
    );
    setError(validationError && newValue.trim() ? validationError : null);
  };

  const handlePrimarySelectChange = (selected: SelectOption | null) => {
    setPrimarySelectValue(selected);
    if (selected) {
      const validationError = validateField(
        selected.value,
        primaryFieldLabel,
        existingNames,
      );
      setError(validationError);
    } else {
      setError(null);
    }
  };

  const isAddDisabled = isPrimarySelect
    ? !primarySelectValue || !secondaryValue || !!error
    : !primaryValue.trim() || !secondaryValue || !!error;

  return (
    <div className={containerClass}>
      <div className={fieldsClass}>
        {isPrimarySelect ? (
          <Select
            label={primaryFieldLabel}
            options={config.primaryField.options || []}
            value={primarySelectValue}
            onChange={handlePrimarySelectChange}
            placeholder={config.primaryField.placeholder}
            status={error ? 'error' : undefined}
            helpText={error}
            style={FIELD_STYLE}
          />
        ) : (
          <TextField
            label={primaryFieldLabel}
            value={primaryValue}
            onChange={handlePrimaryChange}
            required={config.primaryField.required}
            status={error ? 'error' : undefined}
            helpText={error}
            style={FIELD_STYLE}
          />
        )}
        <Select
          label={config.secondaryField.label}
          options={config.secondaryField.options || []}
          value={secondaryValue}
          onChange={setSecondaryValue}
          placeholder={config.secondaryField.placeholder}
          style={FIELD_STYLE}
        />
        <ActionButtons
          onSave={handleAdd}
          onCancel={onCancel}
          disabled={isAddDisabled}
        />
      </div>
    </div>
  );
};

// Item Row Component for displaying and editing saved items
function ItemRow<T extends BaseEditableItem>({
  item,
  config,
  getPrimaryValue,
  getSecondaryValue,
  onDelete,
  onUpdate,
  existingNames,
  fullPrimaryOptions,
  isReadOnly = false,
}: ItemRowProps<T>) {
  const currentPrimaryValue = getPrimaryValue(item);
  const currentSecondaryValue = getSecondaryValue(item);

  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(!currentPrimaryValue.trim());
  const [editPrimaryValue, setEditPrimaryValue] = useState(currentPrimaryValue);
  const [editPrimarySelectValue, setEditPrimarySelectValue] =
    useState<SelectOption | null>(
      fullPrimaryOptions?.find((o) => o.value === currentPrimaryValue) || null,
    );
  const [editSecondaryValue, setEditSecondaryValue] =
    useState<SelectOption | null>(
      config.secondaryField.options?.find(
        (o) => o.value === currentSecondaryValue,
      ) || null,
    );
  const [editError, setEditError] = useState<string | null>(null);

  const isPrimarySelect = config.primaryField.type === 'select';
  const primaryFieldLabel = config.primaryField.label;

  const getEditPrimaryValueString = useCallback(
    () =>
      isPrimarySelect ? editPrimarySelectValue?.value || '' : editPrimaryValue,
    [isPrimarySelect, editPrimarySelectValue, editPrimaryValue],
  );

  // Memoize style classes
  const styleClasses = useMemo(
    () => ({
      container: getStyleClass(
        styles,
        config.stylePrefix,
        'InputRow',
        'editableInputRow',
      ),
      fields: getStyleClass(
        styles,
        config.stylePrefix,
        'InputFields',
        'editableInputFields',
      ),
      row: getStyleClass(styles, config.stylePrefix, 'Row', 'editableRow'),
      rowContent: getStyleClass(
        styles,
        config.stylePrefix,
        'RowContent',
        'editableRowContent',
      ),
      name: getStyleClass(styles, config.stylePrefix, 'Name', 'editableName'),
      tag:
        styles[
          `${config.stylePrefix}${config.secondaryField.name.charAt(0).toUpperCase() + config.secondaryField.name.slice(1)}Tag`
        ] || styles.editableTag,
      actions: getStyleClass(
        styles,
        config.stylePrefix,
        'Actions',
        'editableActions',
      ),
    }),
    [config.stylePrefix, config.secondaryField.name],
  );

  const handleEditSave = () => {
    const primaryVal = getEditPrimaryValueString();
    const validationError = validateField(
      primaryVal,
      primaryFieldLabel,
      existingNames,
      currentPrimaryValue,
    );
    if (validationError) {
      setEditError(validationError);
      return;
    }
    if (!editSecondaryValue) {
      setEditError(`${config.secondaryField.label} is required`);
      return;
    }
    onUpdate(item.id, primaryVal.trim(), editSecondaryValue.value);
    setIsEditing(false);
    setEditError(null);
  };

  const handleEditCancel = () => {
    setEditPrimaryValue(currentPrimaryValue);
    setEditPrimarySelectValue(
      fullPrimaryOptions?.find((o) => o.value === currentPrimaryValue) || null,
    );
    setEditSecondaryValue(
      config.secondaryField.options?.find(
        (o) => o.value === currentSecondaryValue,
      ) || null,
    );
    setIsEditing(false);
    setEditError(null);
  };

  const handleEditPrimaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditPrimaryValue(newValue);
    const validationError = validateField(
      newValue,
      primaryFieldLabel,
      existingNames,
      currentPrimaryValue,
    );
    setEditError(validationError && newValue.trim() ? validationError : null);
  };

  const handleEditPrimarySelectChange = (selected: SelectOption | null) => {
    setEditPrimarySelectValue(selected);
    if (selected) {
      const validationError = validateField(
        selected.value,
        primaryFieldLabel,
        existingNames,
        currentPrimaryValue,
      );
      setEditError(validationError);
    } else {
      setEditError(null);
    }
  };

  const isEditSaveDisabled = isPrimarySelect
    ? !editPrimarySelectValue || !editSecondaryValue || !!editError
    : !editPrimaryValue.trim() || !editSecondaryValue || !!editError;

  if (isEditing) {
    return (
      <div className={styleClasses.container}>
        <div className={styleClasses.fields}>
          {isPrimarySelect ? (
            <Select
              label={primaryFieldLabel}
              options={config.primaryField.options || []}
              value={editPrimarySelectValue}
              onChange={handleEditPrimarySelectChange}
              placeholder={config.primaryField.placeholder}
              status={editError ? 'error' : undefined}
              helpText={editError}
              style={FIELD_STYLE}
            />
          ) : (
            <TextField
              label={primaryFieldLabel}
              value={editPrimaryValue}
              onChange={handleEditPrimaryChange}
              required={config.primaryField.required}
              status={editError ? 'error' : undefined}
              helpText={editError}
              style={FIELD_STYLE}
            />
          )}
          <Select
            label={config.secondaryField.label}
            options={config.secondaryField.options || []}
            value={editSecondaryValue}
            onChange={setEditSecondaryValue}
            placeholder={config.secondaryField.placeholder}
            style={FIELD_STYLE}
          />
          <ActionButtons
            onSave={handleEditSave}
            onCancel={handleEditCancel}
            disabled={isEditSaveDisabled}
          />
        </div>
      </div>
    );
  }

  const primaryLabel = isPrimarySelect
    ? fullPrimaryOptions?.find((o) => o.value === currentPrimaryValue)?.label ||
      currentPrimaryValue
    : toDisplayName(currentPrimaryValue);

  const secondaryLabel =
    config.secondaryField.options?.find(
      (o) => o.value === currentSecondaryValue,
    )?.label || currentSecondaryValue;

  // Use dynamic status-based styling if the secondary field is a status field
  const getTagClassName = () => {
    if (config.secondaryField.name === 'status') {
      // Use status-specific color classes (e.g., completedStatus, inProgressStatus)
      return styles[`${currentSecondaryValue}Status`] || styleClasses.tag;
    }
    return styleClasses.tag;
  };

  return (
    <div
      className={styleClasses.row}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styleClasses.rowContent}>
        <span className={styleClasses.name}>{primaryLabel}</span>
        <Tag value={secondaryLabel} className={getTagClassName()} />
      </div>
      {!isReadOnly && (
        <div
          className={styleClasses.actions}
          style={{ visibility: isHovered ? 'visible' : 'hidden' }}
        >
          <Button variant="anchor" onClick={() => setIsEditing(true)}>
            <IconPencilAlt size="2x" />
          </Button>
          <Button variant="anchor" onClick={() => onDelete(item.id)}>
            <IconBin size="2x" />
          </Button>
        </div>
      )}
    </div>
  );
}

// Main Editable List Tab Component
function EditableListTab<T extends BaseEditableItem>({
  items,
  config,
  getPrimaryValue,
  getSecondaryValue,
  onChange,
  createItem,
  updateItem,
  fullPrimaryOptions,
  readOnlyIds = [],
}: EditableListTabProps<T>) {
  const [showInput, setShowInput] = useState(false);

  // Memoize item names to avoid recalculation on every render
  const itemNames = useMemo(
    () => items.map((item) => getPrimaryValue(item).toLowerCase()),
    [items, getPrimaryValue],
  );

  // Memoize style classes
  const styleClasses = useMemo(
    () => ({
      container: getStyleClass(
        styles,
        config.stylePrefix,
        'sTabContainer',
        'editableTabContainer',
      ),
      header: getStyleClass(
        styles,
        config.stylePrefix,
        'sHeader',
        'editableHeader',
      ),
      list: getStyleClass(styles, config.stylePrefix, 'sList', 'editableList'),
    }),
    [config.stylePrefix],
  );

  const handleAdd = useCallback(
    (primaryValue: string, secondaryValue: string) => {
      const newItem = createItem(
        generateId(config.stylePrefix),
        primaryValue,
        secondaryValue,
      );
      onChange([...items, newItem]);
      setShowInput(false);
    },
    [createItem, config.stylePrefix, onChange, items],
  );

  const handleDelete = useCallback(
    (id: string) => {
      onChange(items.filter((item) => item.id !== id));
    },
    [onChange, items],
  );

  const handleUpdate = useCallback(
    (id: string, primaryValue: string, secondaryValue: string) => {
      onChange(
        items.map((item) =>
          item.id === id
            ? updateItem(item, primaryValue, secondaryValue)
            : item,
        ),
      );
    },
    [onChange, items, updateItem],
  );

  const hideInput = useCallback(() => setShowInput(false), []);
  const showInputRow = useCallback(() => setShowInput(true), []);

  return (
    <div className={styleClasses.container}>
      <div className={styleClasses.header}>
        <span>
          Total {items.length}{' '}
          {items.length === 1 ? config.itemName : config.itemNamePlural}
        </span>
      </div>

      <div className={styleClasses.list}>
        {items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            config={config}
            getPrimaryValue={getPrimaryValue}
            getSecondaryValue={getSecondaryValue}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            existingNames={itemNames}
            fullPrimaryOptions={fullPrimaryOptions}
            isReadOnly={readOnlyIds.includes(item.id)}
          />
        ))}
      </div>

      {showInput && (
        <InputRow
          config={config}
          onAdd={handleAdd}
          onCancel={hideInput}
          existingNames={itemNames}
        />
      )}

      <Button
        variant="default"
        className={styles.addButton}
        onClick={showInputRow}
        disabled={showInput}
      >
        + Add
      </Button>
    </div>
  );
}

export default EditableListTab;
